import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import YupPassword from "yup-password";
import { Container, Row, Col, Card, Button } from "react-bootstrap";

import { register } from "../slices/auth";
import { clearMessage } from "../slices/message";
import {messages} from "../i18n/messages";
import {LOCALES} from "../i18n/locales";
import PasswordField from "./PasswordField";

const Register = () => {
  const [successful, setSuccessful] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { message } = useSelector((state) => state.message);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    matchingPassword: ""
  };

  YupPassword(Yup);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(messages[LOCALES.ENGLISH].field_required),
    lastName: Yup.string().required(messages[LOCALES.ENGLISH].field_required),
    email: Yup.string()
      .email(messages[LOCALES.ENGLISH].invalid_email)
      .required(messages[LOCALES.ENGLISH].field_required),
    password: Yup.string().password()
        .min(8, messages[LOCALES.ENGLISH].password_length)
        .max(30, messages[LOCALES.ENGLISH].password_length)
        .minUppercase(1, messages[LOCALES.ENGLISH].password_upper_case)
        .minNumbers(1, messages[LOCALES.ENGLISH].password_number)
        .minSymbols(1, messages[LOCALES.ENGLISH].password_symbol)
        .required(messages[LOCALES.ENGLISH].field_required),
    matchingPassword: Yup.string()
        .min(8, messages[LOCALES.ENGLISH].password_length)
        .max(30, messages[LOCALES.ENGLISH].password_length)
        .minUppercase(1, messages[LOCALES.ENGLISH].password_upper_case)
        .minNumbers(1, messages[LOCALES.ENGLISH].password_number)
        .minSymbols(1, messages[LOCALES.ENGLISH].password_symbol)
        .oneOf([Yup.ref("password"), null], messages[LOCALES.ENGLISH].password_match)
        .required(messages[LOCALES.ENGLISH].field_required),
  });

  const handleRegister = (formValue) => {
    const { firstName, lastName, email, password, matchingPassword } = formValue;

    setSuccessful(false);
    setIsLoading(true);

    dispatch(register({ firstName, lastName, email, password, matchingPassword }))
      .unwrap()
      .then(() => {
        setSuccessful(true);
        setIsLoading(false);
      })
      .catch(() => {
        setSuccessful(false);
        setIsLoading(false);
      });
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="border-0 shadow-lg" style={{ borderRadius: '24px' }}>
            <div 
              style={{ 
                height: '120px', 
                background: 'var(--primary-gradient)', 
                borderTopLeftRadius: '24px', 
                borderTopRightRadius: '24px' 
              }}
            ></div>
            
            <Card.Body className="px-4 pb-5 pt-0">
              <div 
                className="mx-auto rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm"
                style={{
                  width: '80px',
                  height: '80px',
                  marginTop: '-40px',
                  marginBottom: '20px',
                  border: '4px solid white',
                  fontSize: '2rem',
                  color: '#4f46e5'
                }}
              >
                <i className="bi bi-person-plus-fill"></i>
              </div>

              <h3 className="fw-bold text-center mb-4" style={{ color: 'var(--text-main)' }}>
                Create an Account
              </h3>

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleRegister}
              >
                <Form>
                  {!successful && (
                    <div className="d-flex flex-column gap-3">
                      <Row>
                        <Col md={6}>
                          <label htmlFor="firstName" className="text-muted small fw-bold mb-1">First Name</label>
                          <Field name="firstName" type="input" className="form-control form-control-lg bg-light border-0" style={{ fontSize: '1rem' }} />
                          <ErrorMessage name="firstName" component="div" className="text-danger small mt-1 fw-medium" />
                        </Col>
                        <Col md={6}>
                          <label htmlFor="lastName" className="text-muted small fw-bold mb-1">Last Name</label>
                          <Field name="lastName" type="input" className="form-control form-control-lg bg-light border-0" style={{ fontSize: '1rem' }} />
                          <ErrorMessage name="lastName" component="div" className="text-danger small mt-1 fw-medium" />
                        </Col>
                      </Row>

                      <div>
                        <label htmlFor="email" className="text-muted small fw-bold mb-1">Email</label>
                        <Field name="email" type="email" className="form-control form-control-lg bg-light border-0" style={{ fontSize: '1rem' }} />
                        <ErrorMessage name="email" component="div" className="text-danger small mt-1 fw-medium" />
                      </div>

                      <Row>
                        <Col md={6}>
                          <label htmlFor="password" className="text-muted small fw-bold mb-1">Password</label>
                          <PasswordField name="password" />
                          <ErrorMessage name="password" component="div" className="text-danger small mt-1 fw-medium" />
                        </Col>
                        <Col md={6}>
                          <label htmlFor="matchingPassword" className="text-muted small fw-bold mb-1">Repeat Password</label>
                          <PasswordField name="matchingPassword" />
                          <ErrorMessage name="matchingPassword" component="div" className="text-danger small mt-1 fw-medium" />
                        </Col>
                      </Row>

                      <Button 
                        type="submit" 
                        className="btn-premium w-100 rounded-pill py-2 mt-3" 
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <span className="spinner-border spinner-border-sm me-2"></span>
                        ) : (
                          <i className="bi bi-person-plus-fill me-2"></i>
                        )}
                        Sign Up
                      </Button>
                    </div>
                  )}
                </Form>
              </Formik>

              {message && (
                <div className="mt-4">
                  <div
                    className={`alert ${successful ? "alert-success" : "alert-danger"} rounded-4 border-0 shadow-sm`}
                    role="alert"
                  >
                    <i className={`bi ${successful ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"} me-2`}></i>
                    {message}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Register;
